import React, { useCallback, useEffect, useMemo, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/styles.css';
import '../styles/formularios.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import logoInstituto from '../components/images/logo.png';
import logoSEP from '../components/images/SEP_Logo_2019.svg.png';
import { useLocation, useNavigate } from 'react-router-dom';
import { resolveApiBaseList } from '../utils/apiBase';

function gradoATexto(n) {
  const map = {1: 'Primero', 2: 'Segundo', 3: 'Tercero', 4: 'Cuarto', 5: 'Quinto', 6: 'Sexto'};
  return map[n] ?? n;
}
function statusATexto(s) {
  if (!s) return '';
  return s === 'A' ? 'Activo' : s === 'I' ? 'Inactivo' : s;
}

export default function ConsultarGrupos() {
  const [grupos, setGrupos] = useState([]);                 // [{IdGrupo, Grado, Status, CicloEscolar, TotalAlumnos}]
  const [abierto, setAbierto] = useState(null);             // IdGrupo abierto
  const [alumnosPorGrupo, setAlumnosPorGrupo] = useState({}); // { [idGrupo]: [{IdAlumno, NombreCompleto, CURP}] }
  const [cargandoGrupos, setCargandoGrupos] = useState(false);
  const [cargandoAlumnos, setCargandoAlumnos] = useState(false);
  const navigate = useNavigate();
    const location = useLocation();
    const handleSalir = () => {
      const rol = resolveRolBase(location.pathname);
      if (rol) {
        navigate(`/${rol}/inicio`, { replace: true });
      } else {
        // Fallback por si no se detecta rol ni en path ni en localStorage:
        navigate('/inicio', { replace: true });
      }
    };
  const apiBases = useMemo(() => resolveApiBaseList(), []);

  const fetchJsonWithFallback = useCallback(async (path, options = {}) => {
    let lastError = null;
    for (const base of apiBases) {
      const normalizedBase = base.replace(/\/$/, '');
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      const url = `${normalizedBase}${normalizedPath}`;
      try {
        const response = await fetch(url, options);
        const text = await response.text();

        if (!response.ok) {
          let message = text || `Error ${response.status}`;
          try {
            const parsed = text ? JSON.parse(text) : null;
            if (parsed && typeof parsed === 'object') {
              message = parsed.message || parsed.error || message;
            }
          } catch (parseErr) {
            // keep raw message
          }
          const error = new Error(message);
          error.status = response.status;
          lastError = error;
          console.warn(`⚠️ Falló la petición a ${url}:`, error);
          continue;
        }

        const contentType = response.headers.get('content-type') || '';
        const trimmed = text.trim();
        if (contentType.includes('application/json') || trimmed.startsWith('{') || trimmed.startsWith('[')) {
          const data = trimmed ? JSON.parse(trimmed) : {};
          return { data, url };
        }

        lastError = new Error(`Respuesta inválida desde ${url}`);
        console.warn(`⚠️ Contenido no JSON desde ${url} (content-type: ${contentType || 'desconocido'})`);
      } catch (error) {
        lastError = error;
        console.warn(`⚠️ Error de red al solicitar ${url}:`, error);
      }
    }

    throw lastError || new Error('No se pudo contactar al servidor.');
  }, [apiBases]);

  useEffect(() => {
    (async () => {
      try {
        setCargandoGrupos(true);
        const { data } = await fetchJsonWithFallback('/grupos');
        setGrupos(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('❌ Error cargando grupos:', e);
        setGrupos([]);
      } finally {
        setCargandoGrupos(false);
      }
    })();
  }, [fetchJsonWithFallback]);

  async function toggleGrupo(id) {
    setAbierto(prev => prev === id ? null : id);
    // si no hay alumnos cargados, traerlos
    if (!alumnosPorGrupo[id]) {
      try {
        setCargandoAlumnos(true);
  const { data } = await fetchJsonWithFallback(`/grupos/${id}/alumnos`);
  setAlumnosPorGrupo(prev => ({ ...prev, [id]: Array.isArray(data) ? data : [] }));
      } catch (e) {
  console.error('❌ Error alumnos de grupo:', e);
        setAlumnosPorGrupo(prev => ({ ...prev, [id]: [] }));
      } finally {
        setCargandoAlumnos(false);
      }
    }
  }

  // ====== Exportar Excel / PDF del grupo abierto ======
  const alumnosAbiertos = useMemo(() => alumnosPorGrupo[abierto] || [], [alumnosPorGrupo, abierto]);
  const grupoAbierto = useMemo(() => grupos.find(g => g.IdGrupo === abierto) || null, [grupos, abierto]);

  function exportarExcelGrupo() {
  if (!grupoAbierto) return;

  const wb = XLSX.utils.book_new();
  const gradoTxt = gradoATexto(grupoAbierto.Grado);

  // Encabezado
  const wsData = [
    ['LISTA DE ASISTENCIA'],
    [`Grado: ${gradoTxt}`, `Ciclo escolar: ${grupoAbierto.CicloEscolar}`],
    [],
    ['#', 'Nombre del Alumno'],
  ];

  // Filas (solo nombre)
  (alumnosAbiertos || []).forEach((a, i) => wsData.push([i + 1, a.NombreCompleto]));

  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // Merges para el título (celda A1:D1)
  ws['!merges'] = [{ s: { r:0, c:0 }, e: { r:0, c:3 } }];

  // Anchos de columnas
  ws['!cols'] = [{ wch: 5 }, { wch: 40 }, { wch: 15 }, { wch: 15 }];

  // Congelar hasta encabezado de tabla (fila 4 -> r:3)
  ws['!freeze'] = { xSplit: 0, ySplit: 3 };

  XLSX.utils.book_append_sheet(wb, ws, `Grupo_${grupoAbierto.IdGrupo}`);
  XLSX.writeFile(wb, `asistencia_grupo_${grupoAbierto.IdGrupo}.xlsx`);
  }

  // ====== NUEVA: Exportar lista de asistencia a PDF ======
  function exportarPDFGrupo() {
  if (!grupoAbierto) return;

  const gradoTxt = gradoATexto(grupoAbierto.Grado);
  const doc = new jsPDF('p', 'pt');

  // Título y encabezado
  doc.setFontSize(16);
  doc.text('LISTA DE ASISTENCIA', 40, 40);
  doc.setFontSize(12);
  doc.text(`Grado: ${gradoTxt}`, 40, 62);
  doc.text(`Ciclo escolar: ${grupoAbierto.CicloEscolar}`, 200, 62);

  // Tabla (solo # y Nombre)
  const rows = (alumnosAbiertos || []).map((a, i) => [i + 1, a.NombreCompleto]);

  autoTable(doc, {
    head: [['#', 'Nombre del Alumno']],
    body: rows,
    startY: 80,
    styles: { halign: 'left', valign: 'middle' },
    columnStyles: {
      0: { cellWidth: 40, halign: 'center' },
      1: { cellWidth: 'auto' },
    },
    theme: 'grid',
    headStyles: { fillColor: [240, 240, 240] },
    margin: { left: 40, right: 40 },
  });

  doc.save(`asistencia_grupo_${grupoAbierto.IdGrupo}.pdf`);
  }

  return (
    <div className="container py-4 px-4">
      {/* Encabezado */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <img src={logoInstituto} alt="Logo Instituto" className="logo" />
        <div className="flex-grow-1 text-center">
          <h4 className="mb-0">Instituto Silviano Carrillo</h4>
          <small className="text-muted">Consultar Grupos</small>
        </div>
        <img src={logoSEP} alt="Logo SEP" className="logo" />
      </div>

      {/* Tabla de grupos */}
      <div className="table-responsive mt-4">
        <table className="table table-bordered table-hover shadow-sm bg-white">
          <thead className="table-light text-center">
            <tr>
              <th>ID Grupo</th>
              <th>Grado</th>
              <th>Status</th>
              <th>Ciclo Escolar</th>
              <th>Alumnos</th>
            </tr>
          </thead>
          <tbody className="text-center">
            {cargandoGrupos && (
              <tr><td colSpan="5" className="text-muted">Cargando…</td></tr>
            )}

            {!cargandoGrupos && grupos.map((g) => (
              <React.Fragment key={g.IdGrupo}>
                <tr>
                  <td>{g.IdGrupo}</td>
                  <td>{gradoATexto(g.Grado)}</td>
                  <td>{statusATexto(g.Status)}</td>
                  <td>{g.CicloEscolar}</td>
                  <td>
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => toggleGrupo(g.IdGrupo)}
                    >
                      {abierto === g.IdGrupo ? 'Ocultar Alumnos' : 'Ver Alumnos'}
                    </button>
                  </td>
                </tr>

                {abierto === g.IdGrupo && (
                  <tr>
                    <td colSpan="5">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <small className="text-muted">
                          Total de alumnos: {g.TotalAlumnos ?? (alumnosAbiertos?.length || 0)}
                        </small>

                        <div className="d-flex gap-2">
                          <button className="btn btn-outline-success btn-sm" onClick={exportarExcelGrupo}>
                            <i className="fa fa-file-excel me-1" /> Exportar Excel
                          </button>
                          <button className="btn btn-outline-danger btn-sm" onClick={exportarPDFGrupo}>
                            <i className="fa fa-file-pdf me-1" /> Exportar PDF
                          </button>
                        </div>
                      </div>

                      <div className="table-responsive">
                        <table className="table table-sm table-bordered mb-0">
                          <thead className="table-light">
                            <tr>
                              <th style={{width: 80}}>#</th>
                              <th>Nombre del Alumno</th>
                              <th style={{width: 220}}>CURP</th>
                            </tr>
                          </thead>
                          <tbody>
                            {cargandoAlumnos && (
                              <tr><td colSpan="3" className="text-muted">Cargando alumnos…</td></tr>
                            )}

                            {!cargandoAlumnos && (alumnosAbiertos || []).map((a, idx) => (
                              <tr key={a.IdAlumno}>
                                <td>{idx + 1}</td>
                                <td>{a.NombreCompleto}</td>
                                <td>{a.CURP}</td>
                              </tr>
                            ))}

                            {!cargandoAlumnos && (!alumnosAbiertos || alumnosAbiertos.length === 0) && (
                              <tr><td colSpan="3" className="text-muted">Sin alumnos en este grupo</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}

            {!cargandoGrupos && grupos.length === 0 && (
              <tr><td colSpan="5" className="text-muted">No hay grupos registrados</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Botón salir */}
      <div className="mt-4 text-center">
        <button type="button" className="btn btn-danger px-4" onClick={handleSalir}>
          Salir
        </button>


      </div>
    </div>
  );
}

/* ----------------- Helper para detectar el rol ----------------- */
function resolveRolBase(pathname) {
  // 1) Intenta desde la URL actual
  const roles = ['directora', 'coordinadora', 'docente', 'alumno'];
  const seg = pathname.split('/').filter(Boolean)[0];
  if (roles.includes(seg)) return seg;

  // 2) Respaldo desde localStorage.role (números mapeados a slugs)
  const roleId = Number(localStorage.getItem('role'));
  const map = { 1: 'directora', 2: 'coordinadora', 3: 'docente', 4: 'alumno' };
  return map[roleId] || null;
}
